import React, { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import Footer from "src/components/Footer";
import Header from "src/components/Header";
import Modal from "src/components/Modal";
import { colors } from "src/styles";
import { debugError } from "src/utils/log";
import { TimeAndAttendanceInputForm } from "../InputForm";
import { FirebaseDataService } from "src/services/Firebase/data";
import { Employee } from "src/types";
import { useNavigate, useParams } from "react-router-dom";
import { logInUser } from "src/redux/actions/user";
import { MAXIMO_TIME_ZONE } from "src/config/сonstants";
import FirebaseStorageService from "src/services/Firebase/storage";
import { defaultChecklistsObj, reportPeriods } from "../../../constants";
import moment from "moment";
import { getErrorMsg } from "src/helpers/getErrorMsg";
import { getClockInChecklists, getReportChecklistsObject, reduceChecklists } from "src/services/ChecklistService";
import useLocation from "src/hooks/useLocation";
import { InputForm } from "src/pages/NoUserLogin/UserInfo/styles";
import Input, { InputTypes } from "src/components/Input";
import { VALIDATION_MESSAGES } from "src/constants/validation";
import { useIsMobile } from "src/hooks/useIsMobile";
import { ButtonContainer } from "src/components/DynamicFormContainer/styles";
import { ButtonWrapper } from "src/components/atoms/FormHeader/styles";
import Button from "src/components/Button";

type ClockAction = "initial" | "clockIn" | "clockOut";
type SubmitAction = "clockIn" | "clockOut";

type DailySessionEntry = {
  action: SubmitAction;
  siteName: string;
  timestamp: string;
  needsReview?: boolean;
  autoCloseReason?: string;
  systemEstimated?: boolean;
};

type EmployeeClockStatus = {
  isClockedIn: boolean;
  siteName: string;
  clockInDate?: string;
  clockOutDate?: string;
  staleSession: boolean;
  statusMessage: string;
  durationLabel?: string;
  dailySessions: DailySessionEntry[];
};

type AttendanceSubmitResult = {
  action?: string;
  siteName?: string;
  siteId?: string;
  clockInTime?: string;
  clockOutTime?: string;
  workedHours?: number;
  staleSessionClosed?: {
    clockInTime?: string;
    clockOutTime?: string;
    workedHours?: number;
    autoCloseReason?: string;
    needsReview?: boolean;
  };
  needsReview?: boolean;
  autoCloseReason?: string;
  systemEstimated?: boolean;
};

type ConfirmDialogType = "clockIn" | "clockOut" | "stale";

type ConfirmDialogState = {
  type: ConfirmDialogType;
  action: SubmitAction;
  payload: any;
  title: string;
  message: string;
};

interface TimeAndAttendanceFormProps {
  name: string;
  logo: string;
  setName: (name: string) => void;
  setLogo: (logo: string) => void;
}

export const TimeAndAttendanceForm: React.FC<TimeAndAttendanceFormProps> = ({ name, logo, setName, setLogo }) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    reset,
    clearErrors,
    formState: { isValid, errors },
  } = useForm({
    mode: "all",
  });

  const dispatch = useDispatch();
  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isStatusLoading, setIsStatusLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [labelMessage, setLabelMessage] = useState<string>("");
  const [employeeData, setEmployeeData] = useState<Employee | null>(null);
  const [validationError, setValidationError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [resultMessage, setResultMessage] = useState("");
  const [statusError, setStatusError] = useState("");
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(null);
  const [locationName, setLocationName] = useState("");
  const { clientCode: clientId, locationId: locationId } = useParams();
  const [checklistsData, setChecklistsData] = useState(defaultChecklistsObj);
  const [clockAction, setClockAction] = useState<ClockAction>("initial");
  const [clockStatus, setClockStatus] = useState<EmployeeClockStatus>({
    isClockedIn: false,
    siteName: "",
    staleSession: false,
    statusMessage: "Currently Clocked Out.",
    dailySessions: [],
  });
  const navigate = useNavigate();

  const { coords: userLocation } = useLocation();
  const { isMobile } = useIsMobile();

  const getEmployeeClockedInState = useCallback(
    (employee: Employee) => {
      const onsite = employee?.onsite;

      if (onsite && typeof onsite === "object") {
        if (locationId && Object.prototype.hasOwnProperty.call(onsite, locationId)) {
          return Boolean((onsite as Record<string, unknown>)[locationId]);
        }

        return Object.values(onsite).some((value) => Boolean(value));
      }

      if (typeof onsite === "boolean") {
        return onsite;
      }

      return Boolean(employee?.onsitestate);
    },
    [locationId],
  );

  const getSubmittedChecklistAnswers = (formData: any) => {
    const checklistInfo = checklistsData?.checklistsInfo?.[0];
    if (!checklistInfo) {
      return [];
    }

    const { startIndex, endIndex } = checklistInfo;
    const relevantQuestions = checklistsData.questions.slice(startIndex, endIndex + 1).map((questionGroup: any) => {
      if (Array.isArray(questionGroup?.questions)) {
        return questionGroup;
      }

      return {
        label: questionGroup?.label || "",
        questions: [questionGroup],
      };
    });

    return relevantQuestions.map((questionGroup: any, groupIndex: number) =>
      (questionGroup.questions || []).map((question: any, questionIndex: number) => {
        const radioField = `checklist_${groupIndex}_${questionIndex}`;
        const attachmentField = `checklist_attachment_${groupIndex}_${questionIndex}`;
        const answer = formData[attachmentField] ?? formData[radioField];

        const radioOptions = Array.isArray(question?.radio)
          ? question.radio
          : Array.isArray(question?.radio?.options)
            ? question.radio.options
            : [];

        if (radioOptions.length > 0) {
          const answerIndex = radioOptions.findIndex((option: any) => String(option.value) === String(answer));
          if (answerIndex >= 0) {
            return answerIndex;
          }

          const answerAsNumber = Number(answer);
          return Number.isNaN(answerAsNumber) ? 0 : answerAsNumber;
        }

        return answer || "";
      }),
    );
  };

  const resolveEmployeeId = (fallbackEmployeeId = "") => {
    const fromForm = String(watch("employeeId") || "").trim();
    const fromEmployeeField = String(employeeData?.employeeId || "").trim();
    const fromEmployeeDocId = String(employeeData?.id || "").trim();
    const fromFallback = String(fallbackEmployeeId || "").trim();

    return fromForm || fromEmployeeField || fromEmployeeDocId || fromFallback;
  };

  const formatDurationHours = (fromDate?: string, toDate?: string) => {
    if (!fromDate) {
      return "";
    }

    const start = moment(fromDate);
    const end = toDate ? moment(toDate) : moment();

    if (!start.isValid() || !end.isValid()) {
      return "";
    }

    const durationHours = moment.duration(end.diff(start)).asHours();
    return `${Math.max(durationHours, 0).toFixed(1)} hrs`;
  };

  const buildStatusMessage = (status: {
    isClockedIn: boolean;
    siteName?: string;
    clockInDate?: string;
    durationLabel?: string;
  }) => {
    if (!status.isClockedIn) {
      return "Currently Clocked Out.";
    }

    const siteName = status.siteName || locationName || "this site";
    const timeText = status.clockInDate ? moment(status.clockInDate).format("h:mm A") : "an earlier time";

    return `Currently Clocked In at ${siteName} since ${timeText}.`;
  };

  const getDerivedSubmitAction = (status: EmployeeClockStatus): SubmitAction =>
    status.isClockedIn ? "clockOut" : "clockIn";

  const formatWorkedHoursLabel = (workedHours?: number) => {
    if (typeof workedHours !== "number" || Number.isNaN(workedHours)) {
      return "";
    }

    return `${Math.max(workedHours, 0).toFixed(1)} hrs`;
  };

  const buildClockPayload = (answers: any, action: SubmitAction, employeeId: string) => {
    const hasLatLongLocation =
      Boolean(userLocation) &&
      typeof (userLocation as any)?.lat !== "undefined" &&
      typeof (userLocation as any)?.long !== "undefined";
    const hasGeoLocation =
      Boolean(userLocation) &&
      typeof (userLocation as any)?.latitude !== "undefined" &&
      typeof (userLocation as any)?.longitude !== "undefined";

    return {
      checklists: getReportChecklistsObject({
        checklistsData,
        answers,
      }),
      timestamp: Date.now(),
      clientId,
      locationId,
      employeeId: String(employeeId || "").trim(),
      intendedAction: action,
      siteContext: {
        locationId,
        locationName,
        coordinates: hasLatLongLocation
          ? {
              latitude: Number((userLocation as any).lat),
              longitude: Number((userLocation as any).long),
            }
          : hasGeoLocation
            ? {
                latitude: Number((userLocation as any).latitude),
                longitude: Number((userLocation as any).longitude),
              }
            : null,
      },
    };
  };

  const normalizeStatus = (apiStatus: any): EmployeeClockStatus => {
    const currentSession = apiStatus?.currentSession || apiStatus?.session || null;
    const isClockedIn = Boolean(
      apiStatus?.isClockedIn ?? apiStatus?.hasOpenSession ?? (currentSession && currentSession.status !== "CLOSED"),
    );

    const siteName =
      apiStatus?.siteName || currentSession?.siteName || currentSession?.siteId || locationName || "Unknown Site";
    const clockInDate = apiStatus?.clockInDate || currentSession?.clockInTime;
    const clockOutDate = apiStatus?.clockOutDate || currentSession?.clockOutTime;
    const durationLabel = formatDurationHours(clockInDate, clockOutDate);
    const staleSession = Boolean(
      apiStatus?.staleSession ||
      (clockInDate && !clockOutDate && moment().diff(moment(clockInDate), "hours", true) >= 12),
    );

    const rawDailySessions = apiStatus?.dailySessions || apiStatus?.todaySessions || [];
    const dailySessions: DailySessionEntry[] = Array.isArray(rawDailySessions)
      ? rawDailySessions
          .map((entry: any) => {
            const actionRaw = String(entry?.action || entry?.event || "").toLowerCase();
            const action: SubmitAction = actionRaw.includes("out") ? "clockOut" : "clockIn";
            return {
              action,
              siteName: entry?.siteName || entry?.siteId || siteName,
              timestamp: entry?.timestamp || entry?.clockTime || entry?.clockInTime || entry?.clockOutTime,
              needsReview: Boolean(entry?.needsReview),
              autoCloseReason: entry?.autoCloseReason,
              systemEstimated: Boolean(entry?.systemEstimated || entry?.autoClosed),
            };
          })
          .filter((entry: DailySessionEntry) => Boolean(entry.timestamp))
      : [];

    return {
      isClockedIn,
      siteName,
      clockInDate,
      clockOutDate,
      staleSession,
      durationLabel,
      statusMessage: buildStatusMessage({
        isClockedIn,
        siteName,
        clockInDate,
        durationLabel,
      }),
      dailySessions,
    };
  };

  const getSubmitResultMessage = (submitResult: AttendanceSubmitResult, fallbackAction: SubmitAction) => {
    const actionRaw = String(submitResult?.action || "").toLowerCase();
    const effectiveAction: SubmitAction = actionRaw.includes("out")
      ? "clockOut"
      : actionRaw.includes("in")
        ? "clockIn"
        : fallbackAction;

    const workedLabel = formatWorkedHoursLabel(submitResult?.workedHours);
    const baseClockOutMessage = workedLabel
      ? `Clocked Out - ${workedLabel} logged at ${submitResult.siteName || locationName || "this site"}.`
      : `Clocked Out at ${moment().format("h:mm A")}.`;

    const staleWorkedLabel = formatWorkedHoursLabel(submitResult?.staleSessionClosed?.workedHours);
    const staleReason = submitResult?.staleSessionClosed?.autoCloseReason || submitResult?.autoCloseReason;
    const needsReview = Boolean(submitResult?.staleSessionClosed?.needsReview || submitResult?.needsReview);

    if (actionRaw.includes("stale")) {
      const staleSegment = staleWorkedLabel ? ` Previous session auto-closed at ${staleWorkedLabel}.` : "";
      const reviewSegment = needsReview ? " Marked for manager review." : "";
      const reasonSegment = staleReason ? ` Reason: ${staleReason}.` : "";
      return `Clocked In at ${submitResult.siteName || locationName || "this site"}, ${moment().format("h:mm A")}.${staleSegment}${reviewSegment}${reasonSegment}`;
    }

    if (effectiveAction === "clockOut") {
      const reviewSegment = submitResult?.needsReview ? " Marked for manager review." : "";
      return `${baseClockOutMessage}${reviewSegment}`;
    }

    return `Clocked In at ${submitResult.siteName || locationName || "this site"}, ${moment().format("h:mm A")}.`;
  };

  const loadEmployeeClockStatus = useCallback(
    async (employee: Employee, validatedEmployeeId?: string) => {
      setIsStatusLoading(true);
      setStatusError("");

      const fallbackClockedIn = getEmployeeClockedInState(employee);
      const resolvedEmployeeId =
        String(employee?.employeeId || "").trim() ||
        String(employee?.id || "").trim() ||
        String(validatedEmployeeId || "").trim();

      try {
        const response = await FirebaseDataService.getAttendanceStatus({
          clientId,
          locationId,
          employeeId: resolvedEmployeeId,
        });

        if (response?.status === "success" && response.data) {
          const parsedStatus = normalizeStatus(response.data);

          setClockStatus(parsedStatus);
          setClockAction(parsedStatus.isClockedIn ? "clockOut" : "clockIn");
          setIsStatusLoading(false);
          return parsedStatus;
        }
      } catch (err) {
        setStatusError("Unable to load real-time clock status. Showing best available status.");
      }

      try {
        const response = await FirebaseDataService.getTimeAndAttendanceLogs(clientId || "", locationId || "");
        const attendanceData: any[] = Array.isArray(response?.data) ? (response.data as any[]) : [];
        const employeeKey = resolvedEmployeeId;

        const employeeLogs = attendanceData
          .filter((item: any) => item.employeeId === employeeKey)
          .sort((a: any, b: any) => {
            const aTs = Number(a.timestamp || moment(a.date || a.clockInDate || 0).valueOf());
            const bTs = Number(b.timestamp || moment(b.date || b.clockInDate || 0).valueOf());
            return bTs - aTs;
          });

        const activeSession: any = employeeLogs.find((item: any) => item.clockInDate && !item.clockOutDate);

        const todaySessions = employeeLogs
          .filter((item: any) => {
            const sessionDate = item.date || item.clockInDate || item.clockOutDate;
            return sessionDate && moment(sessionDate).isSame(moment(), "day");
          })
          .flatMap((item: any) => {
            const entries: DailySessionEntry[] = [];
            if (item.clockInDate) {
              entries.push({
                action: "clockIn",
                siteName: item.locationDisplayName || locationName || "Unknown Site",
                timestamp: item.clockInDate,
              });
            }
            if (item.clockOutDate) {
              entries.push({
                action: "clockOut",
                siteName: item.locationDisplayName || locationName || "Unknown Site",
                timestamp: item.clockOutDate,
              });
            }
            return entries;
          })
          .sort((a, b) => moment(a.timestamp).valueOf() - moment(b.timestamp).valueOf());

        const isClockedIn = Boolean(activeSession) || fallbackClockedIn;
        const clockInDate = activeSession?.clockInDate;
        const durationLabel = formatDurationHours(clockInDate);
        const staleSession = Boolean(clockInDate && moment().diff(moment(clockInDate), "hours", true) >= 12);

        const parsedStatus: EmployeeClockStatus = {
          isClockedIn,
          siteName: activeSession?.locationDisplayName || locationName,
          clockInDate,
          clockOutDate: activeSession?.clockOutDate,
          staleSession,
          durationLabel,
          statusMessage: buildStatusMessage({
            isClockedIn,
            siteName: activeSession?.locationDisplayName || locationName,
            clockInDate,
            durationLabel,
          }),
          dailySessions: todaySessions,
        };

        setClockStatus(parsedStatus);
        setClockAction(parsedStatus.isClockedIn ? "clockOut" : "clockIn");
        setIsStatusLoading(false);
        return parsedStatus;
      } catch (err) {
        const fallbackStatus: EmployeeClockStatus = {
          isClockedIn: fallbackClockedIn,
          siteName: locationName,
          staleSession: false,
          statusMessage: fallbackClockedIn
            ? `Currently Clocked In at ${locationName || "this site"}.`
            : "Currently Clocked Out.",
          dailySessions: [],
        };
        setClockStatus(fallbackStatus);
        setClockAction(fallbackStatus.isClockedIn ? "clockOut" : "clockIn");
        setIsStatusLoading(false);
        return fallbackStatus;
      }

      setIsStatusLoading(false);
      return undefined;
    },
    [clientId, locationId, getEmployeeClockedInState, locationName],
  );

  const onSubmit = handleSubmit(async (data: any) => {
    if (!employeeData || isSubmitting || isStatusLoading) {
      return;
    }

    const resolvedEmployeeId = resolveEmployeeId(data?.employeeId);
    if (!resolvedEmployeeId) {
      setError("Employee ID is required before submitting.");
      setSubmitSuccess(false);
      return;
    }

    setError("");
    setLabelMessage("");
    setSubmitSuccess(false);
    setResultMessage("");

    try {
      const answers = getSubmittedChecklistAnswers(data);
      const action: SubmitAction = getDerivedSubmitAction(clockStatus);

      if (clockStatus.isClockedIn && clockStatus.staleSession) {
        const staleSince = clockStatus.clockInDate
          ? moment(clockStatus.clockInDate).format("MMM D, YYYY h:mm A")
          : "an earlier date";
        const duration = clockStatus.durationLabel ? ` (${clockStatus.durationLabel})` : "";

        setConfirmDialog({
          type: "stale",
          action: "clockIn",
          payload: buildClockPayload(answers, "clockIn", resolvedEmployeeId),
          title: "Stale Session Detected",
          message:
            `You appear to still be clocked in from ${staleSince}${duration}. ` +
            "Submitting now will close that stale session as a 12-hour shift and start a new session. If this is not right, contact your supervisor.",
        });
        return;
      }

      if (action === "clockIn") {
        setConfirmDialog({
          type: "clockIn",
          action,
          payload: buildClockPayload(answers, action, resolvedEmployeeId),
          title: "Confirm Clock In",
          message: `Clock in at ${locationName || "this site"} now?`,
        });
        return;
      }

      const sinceText = clockStatus.clockInDate ? moment(clockStatus.clockInDate).format("h:mm A") : "earlier";
      const durationText = clockStatus.durationLabel ? ` (${clockStatus.durationLabel})` : "";

      setConfirmDialog({
        type: "clockOut",
        action,
        payload: buildClockPayload(answers, action, resolvedEmployeeId),
        title: "Confirm Clock Out",
        message:
          `Clock out of ${clockStatus.siteName || locationName || "this site"}? ` +
          `You have been clocked in since ${sinceText}${durationText}.`,
      });
    } catch (error) {
      setError(getErrorMsg(error));
      debugError(error);
    }
  });

  const getChecklists = async () => {
    const { success, data } = await getClockInChecklists({
      clientId: clientId || "",
      locationId: locationId || "",
      sublocationType: "",
      isSublocationTypeDefault: false,
      periodId: reportPeriods.Daily,
    });

    console.log("Fetched checklists:", { success, data });

    if (!success) {
      return;
    }
    const checklistsData = reduceChecklists(data);
    setChecklistsData(checklistsData);
  };

  const getDistance = (
    coord1: { latitude: number; longitude: number },
    coord2: { latitude: number; longitude: number },
  ) => {
    const toRad = (value: number) => (value * Math.PI) / 180;
    const R = 6371e3; // metres
    const φ1 = toRad(coord1.latitude);
    const φ2 = toRad(coord2.latitude);
    const Δφ = toRad(coord2.latitude - coord1.latitude);
    const Δλ = toRad(coord2.longitude - coord1.longitude);
    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const validateDistance = (
    userLocation: { lat: string; long: string } | { latitude: number; longitude: number } | null,
    locationPosition: { lat: string; long: string } | { latitude: number; longitude: number } | null,
  ) => {
    console.log("Validating distance...", userLocation, locationPosition);
    if (!userLocation || !locationPosition) {
      return;
    }

    const normalize = (loc: any) => {
      if ("lat" in loc && "long" in loc) {
        return { lat: parseFloat(loc.lat), long: parseFloat(loc.long) };
      }
      if ("latitude" in loc && "longitude" in loc) {
        return { lat: Number(loc.latitude), long: Number(loc.longitude) };
      }
      return { lat: NaN, long: NaN };
    };

    const { lat: userLat, long: userLong } = normalize(userLocation);
    const { lat: locationLat, long: locationLong } = normalize(locationPosition);

    if (Number.isNaN(userLat) || Number.isNaN(userLong) || Number.isNaN(locationLat) || Number.isNaN(locationLong)) {
      return;
    }

    const distance = getDistance(
      { latitude: userLat, longitude: userLong },
      { latitude: locationLat, longitude: locationLong },
    );

    console.log("Calculated distance (meters):", distance);

    if (distance > 500) {
      // distance in meters
      // navigate("/404");
      navigate("/error?message=You%20may%20only%20clock%20in/out%20at%20work%20location");
      setValidationError("You are too far from the location");
    }
  };

  const validateLocation = async () => {
    if (locationId) {
      setPageLoading(true);
      try {
        const response = await FirebaseDataService.getLocationById(locationId);
        if (!response || !response.data) {
          navigate("/404");
          return;
        }
        const location = response.data;
        setLocationName(location.label);
        if (location && location.position) {
          validateDistance(userLocation, location.position);
        }
      } catch (error) {
        console.error("Error validating washroom:", error);
        navigate("/404");
      } finally {
        setPageLoading(false);
      }
    } else {
      navigate("/404");
    }
  };

  useEffect(() => {
    setError("");
    clearErrors();
    getChecklists();
    validateLocation();
  }, [clientId, locationId, userLocation]);

  const executeClockAction = async (dialog: ConfirmDialogState) => {
    if (!employeeData) {
      return;
    }

    setIsSubmitting(true);
    setError("");
    setSubmitSuccess(false);

    try {
      const response = await FirebaseDataService.submitAttendance(dialog.payload);

      if (response?.status !== "success") {
        throw new Error(response?.message || "Clock request failed");
      }

      const resultPayload = (response?.data || {}) as AttendanceSubmitResult;
      const successMessage = getSubmitResultMessage(resultPayload, dialog.action);

      setSubmitSuccess(true);
      setResultMessage(successMessage);
      setLabelMessage(successMessage);

      await loadEmployeeClockStatus(employeeData, watch("employeeId"));
      setConfirmDialog(null);
      setError("");
    } catch (err) {
      const message = getErrorMsg(err) || "Clock request failed";
      setError(message);
      setLabelMessage(message);
      setResultMessage(message);
      setSubmitSuccess(false);
    }

    setIsSubmitting(false);
  };

  const notifySupervisorForLongShift = async () => {
    setSubmitSuccess(true);
    setError("");
    const supervisorMessage =
      "No clock action was submitted. Please contact your supervisor before continuing if this session should remain open.";
    setResultMessage(supervisorMessage);
    setLabelMessage(supervisorMessage);
    setConfirmDialog(null);
  };

  useEffect(() => {
    return () => {
      setEmployeeData(null);
      setError("");
      reset();
      clearErrors();
      setClockAction("initial");
      setConfirmDialog(null);
    };
  }, []);

  const fetchAndValidateEmployee = useCallback(
    async (employeeId: string) => {
      const normalizedInputEmployeeId = String(employeeId || "").trim();

      if (!normalizedInputEmployeeId) {
        setValidationError("Employee ID is required");
        return;
      }
      setValidationError("");
      try {
        const response = await FirebaseDataService.getEmployeeById(normalizedInputEmployeeId);
        console.log("Employee validation response:", response);
        if (response && response.data) {
          const normalizedEmployeeData: Employee = {
            ...response.data,
            employeeId: response.data.employeeId || normalizedInputEmployeeId,
          };

          setEmployeeData(normalizedEmployeeData);
          setValidationError("");
          setStatusError("");
          setValue("employeeId", normalizedInputEmployeeId, {
            shouldValidate: true,
            shouldDirty: true,
          });
          setValue("firstName", response.data.firstName || "", {
            shouldValidate: true,
            shouldDirty: true,
          });
          setValue("lastName", response.data.lastName || "", {
            shouldValidate: true,
            shouldDirty: true,
          });
          await loadEmployeeClockStatus(normalizedEmployeeData, normalizedInputEmployeeId);
        } else {
          setEmployeeData(null);
          setValidationError("Not a valid employee ID");
        }
      } catch (error) {
        console.error("Employee validation error:", error);
        setEmployeeData(null);
        setValidationError("Error validating employee ID");
        setStatusError("");
        setValue("firstName", "");
        setValue("lastName", "");
      }
    },
    [setValue, loadEmployeeClockStatus],
  );

  const getData = async (code = "") => {
    setPageLoading(true);
    try {
      const { data } = await FirebaseDataService.getClientDataForNoAuthMode(clientId || code);
      if (!data) {
        // navigate("/404");
      }
      const { clientData, labelMessage, maximoTimezone } = data;
      dispatch(logInUser({ clientId: clientData.clientId, maximoTimezone: maximoTimezone || MAXIMO_TIME_ZONE }));
      setLabelMessage(labelMessage);
      if (clientData?.name) {
        setName(clientData.name);
      }
      if (clientData.logo) {
        try {
          const { data: logoUrl } = await FirebaseStorageService.getDownloadURL(
            process.env.REACT_APP_CLIENT_LOGO_FOLDER,
            clientData.logo,
          );
          setLogo(logoUrl);
        } catch (error) {
          // intentionally left empty as there is no handling required for a missing Client logo
        }
      }
    } catch (error) {
      debugError(error);
    }
    setPageLoading(false);
  };

  useEffect(() => {
    getData(clientId || "");
  }, []);

  const handleEmployeeIdValidation = useCallback(
    (employeeId: string) => {
      if (employeeId && employeeId.length > 0) {
        fetchAndValidateEmployee(employeeId);
      } else {
        setEmployeeData(null);
        setValidationError("");
        setStatusError("");
      }
    },
    [fetchAndValidateEmployee],
  );

  const handleProceedClick = () => {
    setClockAction(clockStatus.isClockedIn ? "clockOut" : "clockIn");
    setError("");
    setSubmitSuccess(false);
    setResultMessage("");
  };

  const handleBackClick = () => {
    setClockAction("initial");
    setError("");
    setSubmitSuccess(false);
    setResultMessage("");
    reset();
    setEmployeeData(null);
    setValidationError("");
    setStatusError("");
    setConfirmDialog(null);
    setClockStatus({
      isClockedIn: false,
      siteName: "",
      staleSession: false,
      statusMessage: "Currently Clocked Out.",
      dailySessions: [],
    });
  };

  return (
    <>
      <Container>
        {pageLoading ? (
          <EmptyContainer>
            <Spinner animation="border" />
          </EmptyContainer>
        ) : (
          <>
            <Header name={name} logo={logo} noLink noUserLoginPage />

            {clockAction === "initial" ? (
              <Content>
                <ContentFrame $isMobile={isMobile}>
                  <Title>Time & Attendance</Title>
                  {/* <Subtitle>Please keep your employee ID ready</Subtitle> */}
                  <InputForm $isMobile={isMobile}>
                    <Input
                      controlId="employee-id"
                      type={InputTypes.text}
                      label="Employee ID"
                      validation={register("employeeId", {
                        required: {
                          value: true,
                          message: VALIDATION_MESSAGES.required,
                        },
                        onBlur: (e) => handleEmployeeIdValidation(e.target.value),
                      })}
                    />

                    {validationError && <ValidationError>{validationError}</ValidationError>}
                    {isStatusLoading && <StatusMetaText>Loading current clock status...</StatusMetaText>}
                    {!!statusError && <StatusMetaWarning>{statusError}</StatusMetaWarning>}

                    {employeeData && !validationError && !isStatusLoading && (
                      <StatusSummary>
                        <StatusTitle>Current Status</StatusTitle>
                        <StatusText>{clockStatus.statusMessage}</StatusText>
                        {clockStatus.dailySessions.length > 0 && (
                          <DailyHistory>
                            {clockStatus.dailySessions.map((session, index) => (
                              <li key={`${session.action}-${session.timestamp}-${index}`}>
                                {`${session.action === "clockIn" ? "Clocked in" : "Clocked out"} ${session.siteName} ${moment(session.timestamp).format("h:mm A")}`}
                              </li>
                            ))}
                          </DailyHistory>
                        )}
                      </StatusSummary>
                    )}
                  </InputForm>

                  <ButtonContainer>
                    <ButtonWrapper>
                      <Button
                        text={"Next"}
                        disabled={!isValid || !!validationError || !employeeData || isStatusLoading}
                        loading={isStatusLoading}
                        onClick={handleProceedClick}
                        additionalStyles={{ height: "48px" }}
                      />
                    </ButtonWrapper>
                  </ButtonContainer>
                </ContentFrame>
              </Content>
            ) : (
              <>
                <TimeAndAttendanceInputForm
                  key={clockAction}
                  locationName={locationName}
                  onSubmit={onSubmit}
                  register={register}
                  isValid={isValid}
                  loading={isSubmitting}
                  error={error}
                  errors={errors}
                  labelMessage={resultMessage || labelMessage}
                  validateEmployeeId={handleEmployeeIdValidation}
                  validationError={validationError}
                  setValue={setValue}
                  watch={watch}
                  trigger={trigger}
                  submitSuccess={submitSuccess}
                  checklistsData={checklistsData}
                  clockAction={clockAction}
                  onBack={handleBackClick}
                  isToastVisible={submitSuccess || !!error}
                  submitButtonText="Submit"
                  currentStatusMessage={clockStatus.statusMessage}
                  statusErrorMessage={statusError}
                  isStatusLoading={isStatusLoading}
                  dailySessionHistory={clockStatus.dailySessions}
                />
              </>
            )}

            <StickyFooter />
          </>
        )}
      </Container>

      {confirmDialog && (
        <Modal
          isShowing={Boolean(confirmDialog)}
          hide={() => setConfirmDialog(null)}
          title={confirmDialog.title}
          textContent={confirmDialog.message}
          onApproveClick={() => executeClockAction(confirmDialog)}
          onCancelClick={() => setConfirmDialog(null)}
          approveButtonText={confirmDialog.type === "clockIn" ? "Confirm" : "Confirm"}
          cancelButtonText={confirmDialog.type === "stale" ? "This is not right" : "Cancel"}
          disableApproveButton={isSubmitting}
          disableCancelButton={isSubmitting}
          isLoadingApproveButton={isSubmitting}
        >
          {confirmDialog.type === "stale" && (
            <StaleDialogActionRow>
              <Button
                text="No, I am still working - notify my supervisor"
                variant="text"
                onClick={notifySupervisorForLongShift}
                disabled={isSubmitting}
              />
            </StaleDialogActionRow>
          )}
        </Modal>
      )}
    </>
  );
};

const Container = styled.div`
  background-color: ${colors.white.default};
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  padding-bottom: 60px;
`;

const EmptyContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Content = styled.div`
  padding-top: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;

  @media screen and (max-width: 1369px) {
    padding-top: 100px;
  }
`;

const ContentFrame = styled.div<{ $isMobile?: boolean }>`
  padding: 30px;
  background-color: ${colors.white.default};
  box-shadow: 0px 4px 120px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  width: ${({ $isMobile }) => ($isMobile ? "100%" : "60%")};
`;

const StickyFooter = styled(Footer)`
  position: sticky;
  margin-top: auto;
`;

const ValidationError = styled.div`
  color: #d32f2f;
  font-size: 14px;
  margin-bottom: 12px;
`;

const StatusMetaText = styled.p`
  margin: 0 0 10px;
  font-size: 13px;
  color: #475953;
`;

const StatusMetaWarning = styled.p`
  margin: 0 0 10px;
  font-size: 13px;
  color: #9e5b00;
`;

const StatusSummary = styled.div`
  width: 100%;
  max-width: 617px;
  padding: 14px;
  border: 1px solid #dbe5e1;
  border-radius: 8px;
  background-color: #f7faf9;
`;

const StatusTitle = styled.h3`
  margin: 0 0 8px;
  font-family: "Roboto", sans-serif;
  font-size: 16px;
  color: #1f2d2a;
`;

const StatusText = styled.p`
  margin: 0;
  font-family: "Roboto", sans-serif;
  font-size: 14px;
  color: #1f2d2a;
`;

const DailyHistory = styled.ul`
  margin: 10px 0 0;
  padding-left: 18px;

  li {
    margin-bottom: 4px;
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    color: #374541;
  }
`;

const StaleDialogActionRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 12px;
`;

const Title = styled.h1`
  font-family: "Roboto", sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: #333333;
  margin: 0 0 12px 0;
  text-align: center;
  border-bottom: 1px solid #e0e0e0;
`;

// const Subtitle = styled.p`
//   font-family: "Roboto", sans-serif;
//   font-size: 16px;
//   font-weight: 400;
//   color: #666666;
//   margin: 0 0 24px 0;
//   text-align: center;
//   border-bottom: 1px solid #e0e0e0;
// `;
